'use client'

import { useState, type ReactNode } from 'react'
import { Button } from './button'
import { CheckIcon, ChevronDownIcon } from './icon'
import { IconButton } from './icon-button'
import {
  Command,
  CommandCollection,
  CommandEmpty,
  CommandInput,
  CommandItem,
  CommandList,
} from './command'
import { Popover, PopoverPopup, PopoverTrigger } from './popover'
import { cn } from '../lib/utils'

export interface SearchableSelectOption {
  icon?: ReactNode
  label: string
  searchText?: string
  value: string
}

export interface SearchableSelectProps {
  defaultOpen?: boolean
  defaultSearch?: string
  className?: string
  emptyLabel?: string
  minimal?: boolean
  onValueChange: (value: string) => void
  options: readonly SearchableSelectOption[]
  placeholder?: string
  searchPlaceholder?: string
  triggerIcon?: ReactNode
  triggerVariant?: 'button' | 'icon'
  value?: string
}

export function SearchableSelect({
  defaultOpen = false,
  defaultSearch = '',
  className,
  emptyLabel = 'No options found',
  minimal = false,
  onValueChange,
  options,
  placeholder = 'Select an option',
  searchPlaceholder = 'Search…',
  triggerIcon,
  triggerVariant = 'button',
  value,
}: SearchableSelectProps) {
  const [open, setOpen] = useState(defaultOpen)
  const [search, setSearch] = useState(defaultSearch)
  const selected = options.find((option) => option.value === value)
  const normalizedSearch = search.trim().toLocaleLowerCase()
  const filteredOptions = options.filter((option) =>
    `${option.label} ${option.value} ${option.searchText ?? ''}`
      .toLocaleLowerCase()
      .includes(normalizedSearch),
  )

  return (
    <Popover
      onOpenChange={(nextOpen) => {
        setOpen(nextOpen)
        if (!nextOpen) setSearch('')
      }}
      open={open}
    >
      <PopoverTrigger
        render={
          triggerVariant === 'icon' ? (
            <IconButton
              className={cn(
                'bg-surface-0 text-on-surface-secondary hover:text-on-surface',
                className,
              )}
            />
          ) : (
            <Button
              className={cn(
                'text-on-surface-secondary justify-start',
                minimal &&
                  'min-h-6 px-0 text-xs font-bold focus-visible:ring-1 focus-visible:ring-offset-1',
                className,
              )}
              size="sm"
              variant="text"
            />
          )
        }
      >
        {triggerVariant === 'icon' ? (
          <>
            {triggerIcon ?? <ChevronDownIcon />}
            {selected?.label ?? placeholder}
          </>
        ) : (
          <>
            {selected?.label ?? placeholder}
            <ChevronDownIcon />
          </>
        )}
      </PopoverTrigger>
      <PopoverPopup
        align="start"
        className={cn(
          'bg-surface-3 max-h-80 rounded-[12px] [&_[data-slot=popover-viewport]]:p-0',
          minimal ? 'w-60' : 'w-64',
        )}
        showArrow={false}
        sideOffset={6}
      >
        <div className="flex max-h-80 min-h-0 w-full flex-col overflow-hidden">
          <Command
            autoHighlight={false}
            filteredItems={filteredOptions.map((option) => option.value)}
            items={options.map((option) => option.value)}
            keepHighlight={false}
          >
            <CommandInput
              className="text-sm"
              containerClassName="border-border border-b p-2"
              minimal={minimal}
              onChange={(event) => {
                setSearch(event.target.value)
              }}
              placeholder={searchPlaceholder}
              showSearchIcon={false}
              size={minimal ? 'sm' : 'lg'}
              value={search}
            />
            <CommandList className="max-h-60 not-empty:p-1">
              <CommandEmpty>{emptyLabel}</CommandEmpty>
              <CommandCollection>
                {(optionValue: string) => {
                  const option = options.find(
                    (item) => item.value === optionValue,
                  )
                  if (!option) return null
                  return (
                    <CommandItem
                      className={cn(
                        'min-h-8 gap-2 rounded-md p-3 text-sm',
                        option.value === value && 'bg-primary/8',
                      )}
                      key={option.value}
                      onClick={() => {
                        onValueChange(option.value)
                        setSearch('')
                        setOpen(false)
                      }}
                      value={option.value}
                    >
                      {option.icon ? (
                        <span className="size-5 shrink-0">{option.icon}</span>
                      ) : null}
                      <span>{option.label}</span>
                      {option.value === value ? (
                        <CheckIcon className="ml-auto size-4" />
                      ) : null}
                    </CommandItem>
                  )
                }}
              </CommandCollection>
            </CommandList>
          </Command>
        </div>
      </PopoverPopup>
    </Popover>
  )
}
